import type { MatrixClientConfig } from '$lib/types';
import { DEFAULT_HOMESERVER } from '$lib/constants';
import { AsyncStatus } from '$lib/enums';
import { ConfigService } from '$lib/services/config.service';

class ConfigStore {
	config = $state.raw<MatrixClientConfig>({});
	status = $state<AsyncStatus>(AsyncStatus.IDLE);
	error = $state.raw<unknown>(undefined);
	ignored = $state(false);

	defaultServer = $derived(
		this.config.homeserverList?.[this.config.defaultHomeserver ?? 0] ?? DEFAULT_HOMESERVER
	);

	/**
	 * Fetches `config.json`; failures are exposed through `status`/`error` rather than thrown so
	 * the root layout can offer retry/ignore.
	 * @param fetcher - SvelteKit's fetch during route loading, browser fetch for manual retries
	 */
	async load(fetcher: typeof fetch = fetch): Promise<void> {
		this.status = AsyncStatus.LOADING;
		try {
			this.config = await ConfigService.load(fetcher);
			this.error = undefined;
			this.status = AsyncStatus.SUCCESS;
		} catch (e) {
			this.error = e;
			this.status = AsyncStatus.ERROR;
		}
	}

	/** Continues with an empty configuration after a load failure. */
	ignore(): void {
		this.ignored = true;
	}

	/**
	 * @param server - homeserver name entered by the user
	 * @returns whether login against `server` is permitted by the configuration
	 */
	allowedServer(server: string): boolean {
		const { homeserverList, allowCustomHomeservers } = this.config;
		if (allowCustomHomeservers) return true;
		return homeserverList?.includes(server) === true;
	}
}

export const configStore = new ConfigStore();

export const getClientConfig = (): MatrixClientConfig => configStore.config;
